'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { BreadCrumb } from '~/components/breadcrumb';
import { JobImage } from '~/components/jobs/job-image';
import { isMobile } from 'react-device-detect';
import { JobImages } from '~/components/jobs/job-images';
import { JobInfo } from '~/components/jobs/job-info';
import { ActionButtons } from '~/components/jobs/action-buttons';
import { JobRequirements } from '~/components/jobs/job-requirements';
import { CompanyInformationPanel } from '~/components/jobs/company-information-panel';
import { JobDetail } from '~/types/job-detail';
import { Breadcrumbs } from '~/types/breadcrumb';
import { JobSame } from '~/components/jobs/job_same';
import JobSameCompany from '~/components/jobs/job-same-company';
import JobSimilarity from '~/components/jobs/job_similarity';
import HorizontalSlideForm from '~/components/shared/horizontal-slide-form';
import Youtube from '~/components/shared/youtube';
import JobDetailsFloatingButtons from '~/components/jobs/job-details-floating-buttons';
import ErrorBoundary from '~/components/error-boundary';
import { createJobAccessLog } from '~/lib/jobs';
import { addFavorite } from '~/app/actions/favorites-action';
import UsageFlowPC from './usage-flow-pc';
import { useVerParam } from '~/utils/client/url-params';

type Props = {
  job: JobDetail;
  sameCompanyJobs: JobDetail[];
  othersWatchJobs: JobDetail[];
  majorLicenses: any[];
  experiences: any[];
  areasWithPrefectures: any[];
  prefectures: any[];
  isSignedIn: boolean;
  jobApplicantsJobIds: number[];
  breadCrumbs: Breadcrumbs;
  jobId: string;
  favoriteJobIds: number[];
};

export const PcJobDetail = ({
  job,
  sameCompanyJobs,
  othersWatchJobs,
  majorLicenses,
  experiences,
  areasWithPrefectures,
  prefectures,
  isSignedIn,
  jobApplicantsJobIds,
  breadCrumbs,
  jobId,
  favoriteJobIds,
}: Props) => {
  const [isClientMobile, setIsClientMobile] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const ver = useVerParam();

  useEffect(() => {
    setIsClientMobile(isMobile);
  }, []);

  useEffect(() => {
    if (isClientMobile) {
      createJobAccessLog(Number(job.id));
    }
  }, [isClientMobile, job.id]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFavoriteParam = urlParams.get('favorite');

    if (
      !favoriteJobIds?.includes(job.id) &&
      isFavoriteParam === 'true' &&
      isSignedIn
    ) {
      addFavorite(job.id);
      setIsFavorite(true);
    }
  }, [job.id, isSignedIn, favoriteJobIds]);

  if (isClientMobile) return null;
  return (
    <>
      {!isClientMobile && (
        <>
          <div className="ver2">
            <div className="l-container u-p8">
              <BreadCrumb breadCrumbs={breadCrumbs} />
              <div className="c-border-top"></div>
              <div className="c-panel -job-detail u-mt0 -border-top">
                <div className="c-panel__header -no-border u-p0 -title">
                  <div className="u-flex -items-center u-gap8">
                    <div className="p-job-search__item__tags">
                      {job.new && (
                        <span className="c-keyword -small  -bg-red">NEW</span>
                      )}
                      {job.deadline && (
                        <span className="c-keyword -small -red-border -bg-white">
                          締切間近
                        </span>
                      )}
                    </div>
                  </div>
                  <h1 className="c-panel__header__title -detail">
                    {job.display_company_info}
                    {true ? (
                      <span className="c-panel__header__title__company u-mb5">
                        {job.company.name}
                      </span>
                    ) : (
                      <span className="c-panel__header__title__company u-mb5">
                        社名非公開
                      </span>
                    )}
                    {job.title}
                  </h1>
                  <div className="u-flex -wrap u-mt4">
                    {job.contracts
                      .sort((a, b) => a.position - b.position)
                      .map((contract) => (
                        <span
                          key={contract.id}
                          className="c-keyword -small -green"
                        >
                          {contract.name}
                        </span>
                      ))}
                    {job.features
                      .sort((a, b) => a.position - b.position)
                      .map((feature) => (
                        <span key={feature.id} className="c-keyword -small">
                          {feature.name}
                        </span>
                      ))}
                  </div>
                </div>
                {job.closing && (
                  <div className="u-mt16 u-px20">
                    <div className="p-job-detail__finished c-notice u-mb0">
                      <Image
                        src="/nextjs/images/icons/job_info_image.png"
                        alt="Job Info"
                        width={250}
                        height={200}
                        style={{
                          display: 'inline-block',
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="u-p20 u-flex -justify-between -items-start u-gap50">
                  <ErrorBoundary>
                    <JobInfo job={job} isMobile={false} />
                  </ErrorBoundary>
                  <div>
                    <JobImage job={job} />
                  </div>
                </div>
                <div className="u-flex -justify-center">
                  <ActionButtons
                    job={job}
                    isSignedIn={isSignedIn}
                    jobApplicantsJobIds={jobApplicantsJobIds}
                    isMobile={false}
                    isFavorite={isFavorite}
                    favoriteJobIds={favoriteJobIds}
                    setIsFavorite={(isFavorite: boolean) =>
                      setIsFavorite(isFavorite)
                    }
                  />
                </div>
                {job.youtube_url && (
                  <ErrorBoundary>
                    <Youtube videoId={job.youtube_video_id} isMobile={false} />
                  </ErrorBoundary>
                )}
                {job.attention1 && (
                  <div className="p-job-detail__attention">
                    <div className="p-job-detail__attention__inner">
                      <p>求人の注目ポイント！</p>
                      <ul>
                        <li>{job.attention1}</li>
                        {job.attention2 && <li>{job.attention2}</li>}
                        {job.attention3 && <li>{job.attention3}</li>}
                      </ul>
                    </div>
                  </div>
                )}
                <div className="c-panel__body">
                  <h2 id="job_requirements" className="c-panel__header">
                    募集要項
                  </h2>

                  <div className="p-job-detail__information u-pos-rel u-px0">
                    <ErrorBoundary>
                      <JobRequirements
                        job={job}
                        isSignedIn={isSignedIn}
                        isMobile={false}
                      />
                    </ErrorBoundary>
                    <div className="u-flex -justify-center">
                      <ErrorBoundary>
                        <ActionButtons
                          job={job}
                          isSignedIn={isSignedIn}
                          jobApplicantsJobIds={jobApplicantsJobIds}
                          isMobile={false}
                          isFavorite={isFavorite}
                          favoriteJobIds={favoriteJobIds}
                          setIsFavorite={(isFavorite: boolean) =>
                            setIsFavorite(isFavorite)
                          }
                        />
                      </ErrorBoundary>
                    </div>
                    {job.images_additional?.length > 0 && (
                      <ErrorBoundary>
                        <JobImages job={job} isMobile={false} />
                      </ErrorBoundary>
                    )}
                  </div>
                </div>
              </div>
              <ErrorBoundary>
                {job.service_type == '自社提案' &&
                  ver?.includes('issue_4351') && <UsageFlowPC />}
              </ErrorBoundary>
            </div>
          </div>

          <div>
            {job.job_opened && !isSignedIn && (
              <div
                className="ver2 u-bg-green u-mt40 u-mb20 u-pt20 u-pb40"
                id="horizontal_slide_form_wrapper"
              >
                <div className="l-container u-p8">
                  <div className="c-panel -job-detail">
                    <div className="u-pl20 u-pr20">
                      <ErrorBoundary>
                        <HorizontalSlideForm
                          jobId={job.id}
                          jobTitle={job.title}
                          majorLicenses={majorLicenses}
                          experiences={experiences}
                          isMobile={false}
                          areasWithPrefectures={areasWithPrefectures}
                          prefectures={prefectures}
                        />
                      </ErrorBoundary>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="ver2">
            <div className="l-container u-p8">
              {!!job.company && job.display_company_info && (
                <ErrorBoundary>
                  <CompanyInformationPanel company={job.company} />
                </ErrorBoundary>
              )}
              {sameCompanyJobs.length > 2 && (
                <ErrorBoundary>
                  <JobSameCompany
                    job={job}
                    sameCompanyJobs={sameCompanyJobs}
                    isMobile={false}
                    isSignedIn={isSignedIn}
                    favoriteJobIds={favoriteJobIds}
                  />
                </ErrorBoundary>
              )}
              {!isSignedIn && (
                <ErrorBoundary>
                  <JobSame
                    key={`views/jobs/${jobId}/job_same-${job.updated_at}`}
                    job={job}
                    isMobile={false}
                  />
                </ErrorBoundary>
              )}
              <ErrorBoundary>
                <JobSimilarity
                  jobs={othersWatchJobs}
                  title="この求人を見た人は以下の求人も見ています"
                  isMobile={false}
                  isSignedIn={isSignedIn}
                  favoriteJobIds={favoriteJobIds}
                />
              </ErrorBoundary>
              <div className="u-dis-sp-only">
                <BreadCrumb breadCrumbs={breadCrumbs} />
              </div>
            </div>

            {!job.closing && (
              <div className="c-floating-menu__js">
                <div className="c-floating-menu__apply">
                  <JobDetailsFloatingButtons
                    job={job}
                    isSignedIn={isSignedIn}
                    jobApplicantsJobIds={jobApplicantsJobIds}
                    isFavorite={isFavorite}
                    isMobile={false}
                    favoriteJobIds={favoriteJobIds}
                    setIsFavorite={(isFavorite: boolean) =>
                      setIsFavorite(isFavorite)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
